import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Table,
  Spinner,
  Card,
} from "@radix-ui/themes";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
} from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import {
  fetchAuditorSuppliers,
  fetchApprovedActivitiesForSupplier,
  type AuditSupplier,
  type AuditMarketingActivity,
  type AuditStoreActivity,
} from "../../../state/audit";
import { ProofModal } from "./ProofModal";
import { DownloadProgress } from "./DownloadProgress";
import {
  generateSupplierZip,
  type ZipProgress,
} from "../../../lib/zip-generator";
import { parseLocalDate } from "../../../lib/dates";

// Collapsible section for Level A (Suppliers) and Level B (Marketing Activities)
interface CollapsibleProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  rightContent?: React.ReactNode;
}

function Collapsible({
  title,
  children,
  defaultOpen = false,
  rightContent,
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Box>
      <Flex
        align="center"
        justify="between"
        py="2"
        px="3"
        style={{
          cursor: "pointer",
          backgroundColor: "var(--gray-2)",
          borderRadius: "6px",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Flex align="center" gap="2">
          {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
          {title}
        </Flex>
        {rightContent && (
          <div onClick={(e) => e.stopPropagation()}>{rightContent}</div>
        )}
      </Flex>
      {isOpen && (
        <Box pl="6" pt="3">
          {children}
        </Box>
      )}
    </Box>
  );
}

// Level C: Store Activities Table
interface StoreActivitiesTableProps {
  storeActivities: AuditStoreActivity[];
  periodStart: string;
  periodEnd: string;
  onRowClick: (sa: AuditStoreActivity) => void;
  t: (key: string) => string;
}

function StoreActivitiesTable({
  storeActivities,
  onRowClick,
  t,
}: StoreActivitiesTableProps) {
  return (
    <Table.Root variant="surface" size="1">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>
            {t("audit.storeName")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>
            {t("audit.referenceNumber")}
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {storeActivities.map((sa) => (
          <Table.Row
            key={sa.id}
            style={{ cursor: "pointer" }}
            onClick={() => onRowClick(sa)}
          >
            <Table.Cell>{sa.storeName}</Table.Cell>
            <Table.Cell>{sa.storeReference || "—"}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

// Level B: Marketing Activity
interface MarketingActivitySectionProps {
  activity: AuditMarketingActivity;
  onStoreActivityClick: (
    sa: AuditStoreActivity,
    periodStart: string,
    periodEnd: string,
  ) => void;
  t: (key: string) => string;
}

function MarketingActivitySection({
  activity,
  onStoreActivityClick,
  t,
}: MarketingActivitySectionProps) {
  const formatDate = (dateStr: string) => {
    // Parse and display date-only string via date-fns (see dates-and-periods PRD, F07)
    return parseLocalDate(dateStr).toLocaleDateString();
  };

  const title = (
    <Flex direction="column" gap="1">
      <Text weight="medium" size="2">
        {activity.productName}
        {activity.activityTypeName && ` (${activity.activityTypeName})`}
      </Text>
      <Text size="1" color="gray">
        {activity.periodName}: {formatDate(activity.periodStart)} –{" "}
        {formatDate(activity.periodEnd)}
      </Text>
    </Flex>
  );

  return (
    <Collapsible title={title}>
      <StoreActivitiesTable
        storeActivities={activity.storeActivities}
        periodStart={activity.periodStart}
        periodEnd={activity.periodEnd}
        onRowClick={(sa) =>
          onStoreActivityClick(sa, activity.periodStart, activity.periodEnd)
        }
        t={t}
      />
    </Collapsible>
  );
}

// Level A: Supplier
interface SupplierSectionProps {
  supplier: AuditSupplier;
  onStoreActivityClick: (
    sa: AuditStoreActivity,
    periodStart: string,
    periodEnd: string,
  ) => void;
  onDownload: (supplierId: number) => void;
  t: (key: string) => string;
}

function SupplierSection({
  supplier,
  onStoreActivityClick,
  onDownload,
  t,
}: SupplierSectionProps) {
  const [activities, setActivities] = useState<AuditMarketingActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadActivities = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApprovedActivitiesForSupplier(supplier.id);
      setActivities(data);
      setLoaded(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("audit.failedLoadActivities"),
      );
    } finally {
      setLoading(false);
    }
  }, [supplier.id, loaded]);

  const handleToggle = () => {
    const willExpand = !isExpanded;
    setIsExpanded(willExpand);
    if (willExpand && !loaded) {
      loadActivities();
    }
  };

  return (
    <Card mb="3">
      <Flex
        align="center"
        justify="between"
        py="2"
        px="3"
        style={{ cursor: "pointer" }}
        onClick={handleToggle}
      >
        <Flex align="center" gap="2">
          {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          <Text weight="bold" size="3">
            {supplier.name}
          </Text>
        </Flex>
        <Button
          size="1"
          variant="soft"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(supplier.id);
          }}
        >
          <DownloadIcon />
          {t("common.downloadZip")}
        </Button>
      </Flex>

      {isExpanded && (
        <Box pl="6" pt="3" pb="2">
          {loading && (
            <Flex align="center" gap="2" py="4">
              <Spinner size="2" />
              <Text size="2" color="gray">
                {t("audit.loadingActivities")}
              </Text>
            </Flex>
          )}

          {error && (
            <Text color="red" size="2">
              {error}
            </Text>
          )}

          {!loading && !error && activities.length === 0 && (
            <Text color="gray" size="2">
              {t("audit.noApprovedActivities")}
            </Text>
          )}

          {!loading &&
            !error &&
            activities.map((activity) => (
              <Box key={activity.id} mb="3">
                <MarketingActivitySection
                  activity={activity}
                  onStoreActivityClick={onStoreActivityClick}
                  t={t}
                />
              </Box>
            ))}
        </Box>
      )}
    </Card>
  );
}

// Main Audit Page
export function Audit() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<AuditSupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Proof modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStoreActivity, setSelectedStoreActivity] = useState<{
    sa: AuditStoreActivity;
    periodStart: string;
    periodEnd: string;
  } | null>(null);

  // Download state
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<ZipProgress | null>(
    null,
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSupplier, setDownloadSupplier] =
    useState<AuditSupplier | null>(null);
  const downloadCancelledRef = useRef(false);

  useEffect(() => {
    fetchAuditorSuppliers()
      .then((data) => {
        setSuppliers(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load suppliers");
        setLoading(false);
      });
  }, []);

  const handleStoreActivityClick = (
    sa: AuditStoreActivity,
    periodStart: string,
    periodEnd: string,
  ) => {
    setSelectedStoreActivity({ sa, periodStart, periodEnd });
    setModalOpen(true);
  };

  const handleDownload = async (supplierId: number) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) return;

    // Reset state
    setDownloadSupplier(supplier);
    setDownloadProgress(null);
    setDownloadError(null);
    setDownloadOpen(true);
    downloadCancelledRef.current = false;

    try {
      const result = await generateSupplierZip(supplierId, (progress) => {
        if (downloadCancelledRef.current) return;
        setDownloadProgress(progress);
      });

      if (downloadCancelledRef.current) return;

      // Download complete
      setDownloadProgress({ step: "Complete!", percentage: 100 });

      // Trigger browser download
      saveAs(result.blob, result.filename);
    } catch (err) {
      if (downloadCancelledRef.current) return;
      setDownloadError(
        err instanceof Error ? err.message : "Failed to generate ZIP",
      );
    }
  };

  const handleDownloadCancel = () => {
    downloadCancelledRef.current = true;
    setDownloadOpen(false);
    setDownloadProgress(null);
    setDownloadError(null);
    setDownloadSupplier(null);
  };

  const handleDownloadClose = () => {
    setDownloadOpen(false);
    setDownloadProgress(null);
    setDownloadError(null);
    setDownloadSupplier(null);
  };

  if (loading) {
    return (
      <Box p="6">
        <Flex align="center" justify="center" py="8">
          <Spinner size="3" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="6">
        <Text color="red">{error}</Text>
      </Box>
    );
  }

  return (
    <Box p="6" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <Text size="6" weight="bold" mb="4" as="div">
        {t("audit.title")}
      </Text>

      {suppliers.length === 0 ? (
        <Card>
          <Flex
            direction="column"
            align="center"
            justify="center"
            py="8"
            gap="2"
          >
            <Text size="3" color="gray">
              {t("audit.noSuppliers")}
            </Text>
            <Text size="2" color="gray">
              {t("audit.contactAdmin")}
            </Text>
          </Flex>
        </Card>
      ) : (
        <Flex direction="column" gap="3">
          {suppliers.map((supplier) => (
            <SupplierSection
              key={supplier.id}
              supplier={supplier}
              onStoreActivityClick={handleStoreActivityClick}
              onDownload={handleDownload}
              t={t}
            />
          ))}
        </Flex>
      )}

      {selectedStoreActivity && (
        <ProofModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedStoreActivity(null);
          }}
          storeActivityId={selectedStoreActivity.sa.id}
          storeId={selectedStoreActivity.sa.storeId}
          storeName={selectedStoreActivity.sa.storeName}
          storeReference={selectedStoreActivity.sa.storeReference}
          periodStart={selectedStoreActivity.periodStart}
          periodEnd={selectedStoreActivity.periodEnd}
        />
      )}

      <DownloadProgress
        open={downloadOpen}
        progress={downloadProgress}
        supplierName={downloadSupplier?.name ?? ""}
        error={downloadError}
        onCancel={handleDownloadCancel}
        onClose={handleDownloadClose}
      />
    </Box>
  );
}
